import type { EvidenceItem, EvidenceType } from '../../utils/types';

/**
 * How an issue card presents its evidence (DESIGN.md "Evidence as a summary
 * and a ruled list"). Pure, so the rules are tested without rendering.
 *
 * Every rule here only reorders, shortens or rephrases fields the stage sent;
 * none invents evidence. Specific per-check evidence belongs in the stage.
 */

/** Rows a long list shows before "Show all"; print always shows every row. */
export const EVIDENCE_PREVIEW_ROWS = 5;

export interface EvidenceRow {
  item: EvidenceItem;
  /** The stage's label: a host, a vendor name, a quoted link text, a cookie name. */
  title: string;
  /** A vendor or product name: its rows group together. */
  titleIsName: boolean;
  /** A host, path, cookie or header name, set in mono; prose labels use the body face. */
  titleIsCode: boolean;
  /** The URL or host in `value`, without the protocol, and without the host when the title already names it. */
  location?: string;
  /** The meta fields (and a value that is prose, not a URL) in plain words. */
  notes: string[];
  /** Same vendor as the row above, so the name is not printed twice. */
  continued: boolean;
}

export interface EvidenceModel {
  /** Privacy-policy excerpts, shown as quotes rather than rows. */
  quotes: EvidenceItem[];
  rows: EvidenceRow[];
  /** A sentence every annotated row repeated, said once under the list. */
  sharedNotes: string[];
  /** "7 trackers and 2 cookies"; only for long lists of countable things. */
  summary?: string;
  /** More rows than the preview shows, so the list starts collapsed. */
  collapsible: boolean;
}

const PROTOCOL = /^https?:\/\//i;
const HOST = /^[\w-]+(\.[\w-]+)+$/;
const HOST_OR_URL = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(:\d+)?(\/\S*)?$/i;

/** Strips the protocol, and the host when the title already names it. */
export function shortLocation(value: string, title: string): string {
  const bare = value.replace(PROTOCOL, '');
  const host = title.toLowerCase();
  const lower = bare.toLowerCase();
  if (lower.startsWith(`${host}/`)) return bare.slice(title.length);
  return bare;
}

/** `social_media` → "Social media", `loadedAs` → "loaded as". */
function words(raw: string): string {
  return raw
    .replace(/_/g, ' ')
    // A replacer function, not '$1 $2': scripts/check-purchase-funnel.js reads a quoted "$<digit>" as a price.
    .replace(/([a-z])([A-Z])/g, (_match, lower: string, upper: string) => `${lower} ${upper}`)
    .toLowerCase();
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * One meta field in plain words, or null when the label or the card's finding
 * already says it: `found` is carried by "(no match)" labels and the finding,
 * `controlsChecked` by the "Checked N visible links and buttons" label.
 * An unknown key falls back to "key: value", so nothing a stage adds is lost.
 */
function describeMeta(key: string, value: string | number | boolean, item: EvidenceItem): string | null {
  switch (key) {
    case 'found':
    case 'controlsChecked':
      return null;
    case 'observed':
      // The GPC stage repeats the request URL inside `observed`; the row already shows it.
      return item.value ? String(value).replace(` to ${item.value}`, '') : String(value);
    case 'category':
      return capitalize(words(String(value)));
    case 'loadedAs':
    case 'resourceType':
    case 'note':
    case 'domain':
    case 'signal':
      return String(value);
    case 'viaIframe':
      return value === true ? 'in an iframe' : null;
    case 'initiatedBy':
      return `loaded by ${value}`;
    case 'requests':
      return `${value} request${value === 1 ? '' : 's'}`;
    case 'region':
      return `in the ${value}`;
    case 'iconVerified':
      return value === true ? 'opt-out icon verified' : 'opt-out icon not verified';
    case 'iconState':
      return `icon: ${words(String(value))}`;
    case 'mobileVisible':
      return value === true ? 'visible on mobile' : 'not visible on mobile';
    case 'viaMobileMenu':
      return value === true ? 'in the mobile menu' : null;
    case 'inconspicuous':
      return value === true ? 'inconspicuous' : null;
    case 'offSite':
      return value === true ? 'links off-site' : null;
    case 'notAPolicy':
      return value === true ? 'not a privacy policy' : null;
    default:
      if (typeof value === 'boolean') return value ? words(key) : `not ${words(key)}`;
      return `${words(key)}: ${value}`;
  }
}

/** Drops a note another note in the row already contains ("script" inside "script request …"). */
function withoutContained(notes: string[]): string[] {
  const unique = notes.filter((n, i) => notes.indexOf(n) === i);
  return unique.filter(
    (note) => !unique.some((other) => other !== note && other.toLowerCase().includes(note.toLowerCase())),
  );
}

function toRow(item: EvidenceItem): EvidenceRow {
  const title = item.label;
  const value = item.value && item.value !== title ? item.value : undefined;
  const valueIsPlace = value !== undefined && HOST_OR_URL.test(value);
  const notes: string[] = [];
  if (value && !valueIsPlace) notes.push(value);
  for (const [key, raw] of Object.entries(item.meta ?? {})) {
    const note = describeMeta(key, raw, item);
    if (note) notes.push(note);
  }
  return {
    item,
    title,
    titleIsName: item.type === 'tracker' && !HOST.test(title),
    titleIsCode: HOST.test(title) || title.startsWith('/') || item.type === 'cookie' || item.type === 'header',
    location: value && valueIsPlace ? shortLocation(value, title) : undefined,
    notes: withoutContained(notes),
    continued: false,
  };
}

/** Pulls every row of a named vendor up beside its first row, keeping first-seen order. */
function groupVendors(rows: EvidenceRow[]): EvidenceRow[] {
  const out: EvidenceRow[] = [];
  const placed = new Set<EvidenceRow>();
  for (const row of rows) {
    if (placed.has(row)) continue;
    out.push(row);
    placed.add(row);
    if (!row.titleIsName) continue;
    for (const other of rows) {
      if (!placed.has(other) && other.titleIsName && other.title === row.title) {
        out.push({ ...other, continued: true });
        placed.add(other);
      }
    }
  }
  return out;
}

/**
 * A sentence that every annotated row repeats (the Do Not Sell card's "Title
 * is neither …" note on each candidate link) moves under the list, once.
 * Short tags such as "Marketing" stay on their rows.
 */
function hoistSharedNotes(rows: EvidenceRow[]): { rows: EvidenceRow[]; shared: string[] } {
  const annotated = rows.filter((r) => r.notes.length > 0);
  if (annotated.length < 2) return { rows, shared: [] };
  const shared = annotated[0].notes.filter(
    (note) => note.includes(' ') && note.length > 20 && annotated.every((r) => r.notes.includes(note)),
  );
  if (shared.length === 0) return { rows, shared };
  return {
    rows: rows.map((r) => ({ ...r, notes: r.notes.filter((n) => !shared.includes(n)) })),
    shared: shared.map(capitalize),
  };
}

const COUNT_NOUN: Partial<Record<EvidenceType, [string, string]>> = {
  tracker: ['tracker', 'trackers'],
  cookie: ['cookie', 'cookies'],
  request: ['recipient', 'recipients'],
};

/** "7 trackers and 2 cookies", for a list of three or more countable rows. */
function summarize(rows: EvidenceRow[]): string | undefined {
  if (rows.length < 3 || !rows.every((r) => COUNT_NOUN[r.item.type])) return undefined;
  const counts = new Map<EvidenceType, number>();
  for (const r of rows) counts.set(r.item.type, (counts.get(r.item.type) ?? 0) + 1);
  const parts = [...counts].map(([type, n]) => {
    const [one, many] = COUNT_NOUN[type] as [string, string];
    return `${n} ${n === 1 ? one : many}`;
  });
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function buildEvidence(items: EvidenceItem[]): EvidenceModel {
  const quotes = items.filter((i) => i.type === 'policy-excerpt' && i.value);
  const listed = items.filter((i) => !(i.type === 'policy-excerpt' && i.value));
  const { rows, shared } = hoistSharedNotes(groupVendors(listed.map(toRow)));
  return {
    quotes,
    rows,
    sharedNotes: shared,
    summary: summarize(rows),
    // Collapsing to hide a single row costs more than it saves.
    collapsible: rows.length > EVIDENCE_PREVIEW_ROWS + 1,
  };
}

/** "From https://example.com/privacy" → "From example.com/privacy". */
export function quoteSource(label: string): string {
  return label.replace(/https?:\/\//gi, '');
}
